import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Camera, Loader2, Check, X, ScanLine } from 'lucide-react';
import { cn } from '../../lib/utils';
import { scanLineupCard } from '../../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { usePremium } from '../../hooks/usePremium';

export const ScanLineupDialog = ({ open, onOpenChange, onPlayersScanned }) => {
  const [step, setStep] = useState('capture'); // capture, scanning, review
  const [scannedPlayers, setScannedPlayers] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { isPremium, loading: premLoading } = usePremium();

  // If a free user tries to open this, bounce them to /upgrade.
  useEffect(() => {
    if (!open || premLoading) return;
    if (!isPremium) {
      onOpenChange(false);
      navigate('/upgrade?feature=AI%20Scan%20Lineup%20Card');
    }
  }, [open, isPremium, premLoading, onOpenChange, navigate]);

  const reset = () => {
    setStep('capture');
    setScannedPlayers([]);
    setScanning(false);
    setPreviewUrl(null);
  };

  const handleClose = (open) => {
    if (!open) reset();
    onOpenChange(open);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setStep('scanning');
    setScanning(true);

    try {
      // Resize image to max 1200px to avoid payload size limits
      const resizeImage = (file) => new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const MAX = 1200;
          let w = img.width, h = img.height;
          if (w > MAX || h > MAX) {
            if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
            else { w = Math.round(w * MAX / h); h = MAX; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', 0.8).split(',')[1]);
        };
        img.src = URL.createObjectURL(file);
      });

      const base64 = await resizeImage(file);
      try {
        const result = await scanLineupCard(base64);
          if (result.players && result.players.length > 0) {
            const POS_SET = new Set(['P','C','1B','2B','3B','SS','LF','CF','RF','DH','DP','EH','FLEX']);
            setScannedPlayers(result.players.map((p, i) => {
              let name = p.name || '';
              let pos = p.position || '';
              let num = p.jersey_number || '';
              // If name is actually a position abbreviation, swap
              if (POS_SET.has(name.toUpperCase()) && !pos) {
                pos = name.toUpperCase();
                name = '';
              }
              return { name, jersey_number: num, position: pos, included: !!name, order: i + 1 };
            }));
            setStep('review');
            toast.success(`Found ${result.players.length} players!`);
          } else {
            toast.error('No players found. Try a clearer photo.');
            setStep('capture');
          }
        } catch (err) {
          toast.error('Failed to scan lineup card. Try again.');
          setStep('capture');
        }
        setScanning(false);
    } catch (err) {
      toast.error('Error reading image');
      setScanning(false);
      setStep('capture');
    }
  };

  const togglePlayer = (idx) => {
    setScannedPlayers(prev => prev.map((p, i) => i === idx ? { ...p, included: !p.included } : p));
  };

  const updatePlayerName = (idx, name) => {
    setScannedPlayers(prev => prev.map((p, i) => i === idx ? { ...p, name } : p));
  };

  const updatePlayerNumber = (idx, jersey_number) => {
    setScannedPlayers(prev => prev.map((p, i) => i === idx ? { ...p, jersey_number } : p));
  };

  const updatePlayerPosition = (idx, position) => {
    setScannedPlayers(prev => prev.map((p, i) => i === idx ? { ...p, position } : p));
  };

  const POSITION_ABBREVS = new Set(['P','C','1B','2B','3B','SS','LF','CF','RF','DH','DP','EH','SP','RP','OF','IF','PH','PR']);

  const handleConfirm = () => {
    const players = scannedPlayers
      .filter(p => p.included && p.name.trim())
      .map(p => {
        let name = p.name.trim();
        let pos = p.position?.trim() || null;
        // If the name looks like a position abbreviation, swap it to position field
        if (POSITION_ABBREVS.has(name.toUpperCase()) && !pos) {
          pos = name.toUpperCase();
          name = ''; // Will be filtered out
        }
        // If position is not a valid abbreviation, ignore it
        if (pos && !POSITION_ABBREVS.has(pos.toUpperCase())) pos = null;
        return { name, jersey_number: p.jersey_number?.trim() || null, position: pos };
      })
      .filter(p => p.name && !POSITION_ABBREVS.has(p.name.toUpperCase()));
    if (players.length === 0) {
      toast.error('No players selected');
      return;
    }
    onPlayersScanned(players);
    handleClose(false);
    const posCount = players.filter(p => p.position).length;
    toast.success(`${players.length} players added!${posCount ? ` ${posCount} field positions detected.` : ''}`);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-stone-800 border-stone-700 max-w-md" aria-describedby="scan-desc">
        <DialogHeader>
          <DialogTitle className="text-green-400 text-center text-xl flex items-center justify-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <ScanLine size={22} /> SCAN LINEUP CARD
          </DialogTitle>
        </DialogHeader>

        {step === 'capture' && (
          <div className="space-y-4 py-4">
            <div className="bg-stone-700/50 rounded-xl p-8 text-center border-2 border-dashed border-stone-600">
              <Camera size={48} className="mx-auto mb-3 text-stone-400" />
              <p className="text-stone-300 font-semibold mb-1">Take a photo of the lineup card</p>
              <p className="text-stone-500 text-xs mb-4">Our AI will read the player names and numbers</p>
              <Button onClick={() => fileInputRef.current?.click()} className="bg-green-700 hover:bg-green-600 font-bold" data-testid="scan-open-camera-btn">
                <Camera size={16} className="mr-2" /> Open Camera
              </Button>
              <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileSelect} data-testid="scan-file-input" />
            </div>
            <p className="text-stone-500 text-[10px] text-center">Works with handwritten or printed lineup cards</p>
          </div>
        )}

        {step === 'scanning' && (
          <div className="space-y-4 py-4 text-center">
            {previewUrl && <img src={previewUrl} alt="Lineup card" className="w-full max-h-48 object-contain rounded-lg" />}
            <div className="flex items-center justify-center gap-3">
              <Loader2 size={24} className="animate-spin text-green-400" />
              <p className="text-stone-300 font-semibold">Reading lineup card...</p>
            </div>
            <p className="text-stone-500 text-xs">AI is extracting player names and numbers</p>
          </div>
        )}

        {step === 'review' && (
          <div className="space-y-3 py-2">
            <p className="text-stone-400 text-xs text-center">Review and edit before adding. Tap a name to fix it.</p>
            <div className="max-h-[350px] overflow-y-auto space-y-1.5">
              {scannedPlayers.map((p, i) => (
                <div key={i} className={cn('flex items-center gap-2 p-2 rounded-lg transition-all',
                  p.included ? 'bg-stone-700/70' : 'bg-stone-700/30 opacity-50'
                )} data-testid={`scanned-player-${i}`}>
                  <span className="w-6 h-6 rounded-full bg-green-800 text-green-300 flex items-center justify-center text-xs font-bold">{i + 1}</span>
                  <Input value={p.name} onChange={(e) => updatePlayerName(i, e.target.value)}
                    className="flex-1 bg-stone-600 border-stone-500 text-white text-sm h-8" data-testid={`scanned-name-${i}`} />
                  <Input value={p.jersey_number || ''} onChange={(e) => updatePlayerNumber(i, e.target.value)}
                    placeholder="#" className="w-12 bg-stone-600 border-stone-500 text-white text-sm h-8 text-center" data-testid={`scanned-number-${i}`} />
                  <Input value={p.position || ''} onChange={(e) => updatePlayerPosition(i, e.target.value)}
                    placeholder="Pos" className="w-14 bg-stone-600 border-stone-500 text-amber-400 text-sm h-8 text-center font-bold" data-testid={`scanned-pos-${i}`} />
                  <button onClick={() => togglePlayer(i)}
                    className={cn('w-7 h-7 rounded-full flex items-center justify-center transition-all',
                      p.included ? 'bg-green-600 text-white' : 'bg-stone-600 text-stone-400'
                    )} data-testid={`toggle-player-${i}`}>
                    {p.included ? <Check size={14} /> : <X size={14} />}
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 text-stone-300 border-stone-600" onClick={() => { reset(); }} data-testid="scan-retake-btn">
                Retake
              </Button>
              <Button className="flex-1 bg-green-600 hover:bg-green-500 font-bold" onClick={handleConfirm}
                disabled={scannedPlayers.filter(p => p.included && p.name.trim()).length === 0} data-testid="scan-confirm-btn">
                Add {scannedPlayers.filter(p => p.included && p.name.trim()).length} Players
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
